import React, { useEffect, useState } from 'react';
import clsx from "clsx";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { Divider, Form, Spin, Input, Checkbox, Radio, Space, Button as AntdButton, Steps } from "antd";
import {
    MinusCircleOutlined,
    PlusOutlined
} from "@ant-design/icons";
import styled from "styled-components";
import { getContent, toggleSpinner, editContent, setContent, getOpportunity, getProgramById } from "../../redux/actions";
import StyledText from "../../components/StyledText";
import Button from "../../components/Button";
import Select from "../../components/CustomSelect"
import { getLocalStorageItem, numericValidator } from "../../utils/utilities";
import {
    toggleSpinnerSelector,
    opportunityDataSelector,
    savedContentListSelector,
    savedContentDataSelector,
    programDataSelector
} from "../../redux/selectors";
import grant from "../../assets/grant.svg";
import GrantDetails from "../charityCreate/charityProgramReview/Grants"
import VolunteeringDetails from "../charityCreate/charityProgramReview/Volunteer";
import InKindDetails from "../charityCreate/charityProgramReview/InKind";
import {
    useAppDispatch,
    useAppSelector
} from "../../redux/store";

const formItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 12 },
};

const { TextArea } = Input;

const CharityFinalReport = ({ className }) => {
    const baseClassName = clsx("charityFinalReport", className);
    const intl = useIntl();
    const [formData, setFormData] = useState({});
    const [monetaryUnitError, setMonetaryUnitError] = useState(null);
    const [donationUseError, setDonationUseError] = useState(null);
    const [inKindUseError, setInKindUseError] = useState(null);
    const [volunteeringUseError, setVolunteeringUseError] = useState(null);
    const [savedClicked, setSavedClicked] = useState(false);
    const opportunityData = useAppSelector(opportunityDataSelector) || {}
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const programData = useAppSelector(programDataSelector) || {};
    const showSpinner = useAppSelector(toggleSpinnerSelector);
    const { ID } = useParams();
    const { ID: contentId,
        actualDonationUse,
        actualInKindUse,
        actualVolunteerUse,
        inKind,
        volunteering,
        cashGiving,
        inputsChange
    } = useAppSelector(savedContentDataSelector) || {}
    const { opportunityID } = JSON.parse(getLocalStorageItem('contentData') || '{}');
    const { opportunity:
        { volunteerUse,
            inKindUse,
            donationUse,
            additionalFundsSource,
            fundingSecured,
            oppFullBudgetNotRequired,
            programID,
            inKind: oppInKind,
            volunteering: oppVolunteering,
            cashGiving: oppCashGiving,
            cashTargetCurrencyCode,
            wholeProjectFunding,
            totalCashRequested
        } = {} }
        = opportunityData || {};

    const { volunteeringSkillList, volunteeringType, currencySymbol, inKindOffering } = programData || {};
    const { message } = useAppSelector(savedContentListSelector) || {};

    useEffect(() => {
        if (contentId) {
            const group = [];
            if (inKind === 'Y') group.push('inKind');
            if (volunteering === 'Y') group.push('volunteering');
            if (cashGiving === 'Y') group.push('cashGiving');
            const data = {
                checkboxGroup: group,
                ...(actualDonationUse && { donationUse: actualDonationUse }),
                ...(actualInKindUse ? { inKindUse: actualInKindUse } : { inKindUse: [{ inKindOfferingDescription: undefined, inKindActivityDescription: "" }] }),
                ...(actualVolunteerUse ? { volunteerUse: actualVolunteerUse } : { volunteerUse: [{ volunteerUnit: undefined, volunteerValue: "" }] }),
                ...(inputsChange === 'Y' && { inputsChange: 'Y' }),
                ...(inputsChange === 'N' && { inputsChange: 'N' }),
            };
            form.setFieldsValue(data);
            setFormData(data);
        }
    }, [contentId]);


    useEffect(() => {
        const validateMonetaryUnitSum = () => {
            const donationUse = form.getFieldValue('donationUse');

            const sumMonetaryUnits = donationUse?.reduce((sum, item) => {
                const monetaryUnitValue = parseFloat(item?.monetaryUnit || 0);
                return sum + monetaryUnitValue;
            }, 0);

            const differenceMonetaryUnits = parseFloat(totalCashRequested) - sumMonetaryUnits;
            if (sumMonetaryUnits !== parseFloat(totalCashRequested) && donationUse?.length && differenceMonetaryUnits) {
                setMonetaryUnitError(`The sum of these costs is ${currencySymbol}${Math.abs(differenceMonetaryUnits)} ${differenceMonetaryUnits < 0 ? 'more' : 'less'} than how much you are applying for.`);
            } else {
                setMonetaryUnitError(null);
            }
        };

        validateMonetaryUnitSum();
    }, [form, form.getFieldValue('donationUse'), form.getFieldValue('totalCashRequested')]);

    useEffect(() => {
        const validateFormList = () => {
            const cashGiving = formData?.checkboxGroup?.includes("cashGiving");
            const donationUse = form.getFieldValue('donationUse');
            const inKind = formData?.checkboxGroup?.includes("inKind");
            const inKindUse = form.getFieldValue('inKindUse');
            const volunteering = formData?.checkboxGroup?.includes("volunteering");
            const volunteerUse = form.getFieldValue('volunteerUse');
            if (cashGiving && !donationUse?.length) {
                setDonationUseError('Please enter at least 1 row');
            } else {
                setDonationUseError(null);
            }
            if (inKind && !inKindUse?.length) {
                setInKindUseError('Please enter at least 1 row');
            } else {
                setInKindUseError(null);
            }
            if (volunteering && !volunteerUse?.length) {
                setVolunteeringUseError('Please enter at least 1 row');
            } else {
                setVolunteeringUseError(null);
            }
        };

        validateFormList();
    }, [form, formData]);

    useEffect(() => {
        if (message && !savedClicked) {
            dispatch(setContent(''))
            dispatch(toggleSpinner(false));
            navigate(`/impact-maker/content/final/outputs/${ID}`);
        } else if (message && savedClicked) {
            dispatch(setContent(''))
            dispatch(toggleSpinner(false));
            navigate(`/impact-maker/content`);
        }
    }, [message])

    useEffect(() => {
        if (opportunityID) {
            document?.documentElement?.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            dispatch(getContent({ contentID: ID }));
            dispatch(getOpportunity(opportunityID));
        }
    }, [opportunityID]);


    useEffect(() => {
        if (programID) {
            dispatch(getProgramById(programID));
        }
    }, [programID]);

    const onFinishFailed = (e) => {
        console.log("Failed:", e);
    };

    const handleProceed = (e) => {
        form.validateFields(['inputsChange']).then(values => {
            const { inputsChange: formInputsChange } = formData || {};
            if (formInputsChange === undefined) {
                form.setFields([
                    {
                        name: 'inputsChange',
                        errors: [intl.formatMessage({ id: 'please_select' })],
                    },
                ]);
                return
            }
            if ((inKindUseError || donationUseError || volunteeringUseError || monetaryUnitError) && values?.inputsChange === "Y") return;
            let data;
            if (values?.inputsChange === "N") {
                data = {
                    contentId: ID,
                    inKind: oppInKind,
                    volunteering: oppVolunteering,
                    cashGiving: oppCashGiving,
                    cashTargetCurrencyCode: currencySymbol,
                    actualDonationUse: donationUse,
                    actualVolunteerUse: volunteerUse,
                    actualInKindUse: inKindUse,
                    inputsChange: 'N',
                    action: "Draft"
                }
            }
            else {
                data = {
                    contentId: ID,
                    inKind: formData?.checkboxGroup?.includes("inKind") ? "Y" : "N",
                    volunteering: formData?.checkboxGroup?.includes("volunteering") ? "Y" : "N",
                    cashGiving: formData?.checkboxGroup?.includes("cashGiving") ? "Y" : "N",
                    cashTargetCurrencyCode: currencySymbol,
                    actualDonationUse: formData?.donationUse,
                    actualVolunteerUse: formData?.volunteerUse,
                    actualInKindUse: formData?.inKindUse,
                    inputsChange: 'Y',
                    action: "Draft"
                }
            }
            dispatch(editContent(data));
            dispatch(toggleSpinner(true));
        });
    }

    const handleSaveAndExit = () => {
        setSavedClicked(true);
        setTimeout(() => {
            form.submit();
        }, 300);
    }

    const handlePrevious = () => {
        navigate(`/impact-maker/content/final/start/${ID}`)
    }

    const onFormChange = (_, allValues) => {
        setFormData(allValues);
    };

    const { checkboxGroup, inputsChange: formInputsChange } = formData || {};
    const grantBudgetPercentage = Math.round((totalCashRequested / wholeProjectFunding) * 100);

    return (
        <div className={baseClassName}>
            {showSpinner && (
                <div className="overlay">
                    <Spin className="spinner" size="large" spinning={showSpinner} />
                </div>
            )}
            <Steps
                current={0}
                items={[
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_1" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3c" className='mainTitle'>
                            {intl.formatMessage({ id: "inputs_n_outputs" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_2" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "objectives_n_outcomes" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_3" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "case_studies" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_4" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "next_steps" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_5" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "preview_n_submit" })}
                        </StyledText>,
                    },
                ]}
            />
            <div className='contentContainer'>
                <div>
                    <StyledText as="p" variant="H1" className='mainTitle'>
                        {intl.formatMessage({ id: "inputs" })}
                    </StyledText>
                    <Form
                        {...formItemLayout}
                        labelAlign="left"
                        onFinish={handleProceed}
                        onFinishFailed={onFinishFailed}
                        form={form}
                        className="formContainer"
                        scrollToFirstError
                        onValuesChange={onFormChange}
                        initialValues={{
                            inKindUse: [
                                {
                                    inKindOfferingDescription: undefined,
                                    inKindActivityDescription: "",
                                },
                            ],
                            volunteerUse: [
                                {
                                    volunteerUnit: undefined,
                                    volunteerValue: "",
                                },
                            ],
                            donationUse: [
                                {
                                    monetaryUnit: '',
                                    numberOfItems: '',
                                    itemDescription: ''
                                }
                            ]
                        }}
                    >
                        <div className="mainContentContainer">
                            <div className="contentContainer">
                                <StyledText as="p" variant="B3">
                                    {intl.formatMessage({ id: "below_is_the_support_you_requested_from_the_company_in_your_application" })}
                                </StyledText>
                                <GrantDetails
                                    currencySymbol={cashTargetCurrencyCode}
                                    grantImage={grant}
                                    totalCashRequested={totalCashRequested}
                                    wholeProjectFunding={wholeProjectFunding}
                                    grantBudgetPercentage={grantBudgetPercentage}
                                    donationUse={donationUse}
                                    additionalFundsSource={additionalFundsSource}
                                    fundingSecured={fundingSecured}
                                    oppFullBudgetNotRequired={oppFullBudgetNotRequired}
                                    intl={intl}
                                />
                                {volunteerUse?.length > 0 && <VolunteeringDetails
                                    title={intl.formatMessage({ id: "volunteering" })}
                                    volunteerUse={volunteerUse}
                                />}
                                {inKindUse?.length > 0 && <InKindDetails
                                    title={intl.formatMessage({ id: "inkind" })}
                                    inKindUse={inKindUse}
                                />}
                                <StyledText as="p" variant="B3d">
                                    {intl.formatMessage({ id: "did_any_of_your_plans_change" })}
                                </StyledText>
                                <Form.Item
                                    name="inputsChange"
                                    rules={[
                                        { required: true, message: intl.formatMessage({ id: "enter" }) }
                                    ]}
                                >
                                    <Radio.Group className="radioGroup">
                                        <Radio className="radio" value="Y">
                                            <StyledText as="p" variant="B3">
                                                {intl.formatMessage({ id: "yes" })}
                                            </StyledText>
                                        </Radio>
                                        <Radio className="radio" value="N">
                                            <StyledText as="p" variant="B3">
                                                {intl.formatMessage({ id: "no" })}
                                            </StyledText>
                                        </Radio>
                                    </Radio.Group>
                                </Form.Item>
                                {formInputsChange === "Y" && <>
                                    <StyledText as="p" variant="B3d">
                                        {intl.formatMessage({ id: "please_select_and_enter_your_final_inputs" })}
                                    </StyledText>
                                    <Form.Item
                                        name="checkboxGroup"
                                    >
                                        <Checkbox.Group className='checkboxGroup'>
                                            <Checkbox key="cashGiving" value="cashGiving">
                                                {intl.formatMessage({ id: "grant" })}
                                            </Checkbox>
                                            <Checkbox key="inKind" value="inKind">
                                                {intl.formatMessage({ id: "in_kind" })}
                                            </Checkbox>
                                            <Checkbox key="volunteering" value="volunteering">
                                                {intl.formatMessage({ id: "volunteering" })}
                                            </Checkbox>
                                        </Checkbox.Group>
                                    </Form.Item>
                                    {checkboxGroup?.includes('cashGiving') && <>
                                        <StyledText as="p" variant="B3d">{intl.formatMessage({ id: "please_update_your_expenditure" })}</StyledText>
                                        <Form.List name="donationUse">
                                            {(fields, { add, remove }) => (
                                                <>
                                                    {fields.map(({ key, name, ...restField }) => (
                                                        <Space key={key} style={{ display: 'flex', marginBottom: 0 }} className='optionsContainer'>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'monetaryUnit']}
                                                                className="formItemInput"
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        message: intl.formatMessage({ id: "enter" }),
                                                                    },
                                                                    { validator: numericValidator, message: intl.formatMessage({ id: 'please_enter_numeric_value' }) }
                                                                ]}
                                                            >
                                                                <Input addonBefore={cashTargetCurrencyCode}
                                                                    placeholder={intl.formatMessage({ id: "enter_amount" })}
                                                                />
                                                            </Form.Item>
                                                            <StyledText as="p" variant="B3" className='for'>
                                                                {intl.formatMessage({ id: "for" })}
                                                            </StyledText>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'numberOfItems']}
                                                                className="formItemTextFieldPart"
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        message: intl.formatMessage({ id: "enter" }),
                                                                    },
                                                                    { validator: numericValidator, message: intl.formatMessage({ id: 'please_enter_numeric_value' }) }
                                                                ]}
                                                            >
                                                                <TextArea rows={1} placeholder={intl.formatMessage({ id: "enter_number_of_items_or_activity" })} />
                                                            </Form.Item>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'itemDescription']}
                                                                className="formItemTextFieldPart"
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        message: intl.formatMessage({ id: "enter" }),
                                                                    },
                                                                ]}
                                                            >
                                                                <TextArea rows={1} placeholder={intl.formatMessage({ id: "enter_description" })} />
                                                            </Form.Item>
                                                            <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#04ac9c' }} className='for' />
                                                        </Space>
                                                    ))}
                                                    {donationUseError && (
                                                        <StyledText as="p" variant="B3" className="errorText">
                                                            {donationUseError}
                                                        </StyledText>
                                                    )}
                                                    {monetaryUnitError && (
                                                        <StyledText as="p" variant="B3" className="errorText">
                                                            {monetaryUnitError}
                                                        </StyledText>
                                                    )}
                                                    <Form.Item>
                                                        <AntdButton htmlType='button' className='addButton' type="dashed" onClick={() => add()} block icon={<PlusOutlined fill="red" />}>
                                                            <StyledText as="p" variant="B3">
                                                                {intl.formatMessage({ id: "add" })}
                                                            </StyledText>
                                                        </AntdButton>
                                                    </Form.Item>
                                                </>
                                            )}
                                        </Form.List>
                                    </>}
                                    {checkboxGroup?.includes('inKind') && <>
                                        <StyledText as="p" variant="B3d">{intl.formatMessage({ id: "please_update_the_items_or_services_you_used" })}</StyledText>
                                        <Form.List name="inKindUse">
                                            {(fields, { add, remove }) => (
                                                <>
                                                    {fields.map(({ key, name, ...restField }) => (
                                                        <Space key={key} style={{ display: 'flex', marginBottom: 0 }} className='optionsContainer'>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'inKindOfferingDescription']}
                                                                className="formItem"
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        message: intl.formatMessage({ id: "enter" }),
                                                                    },
                                                                ]}
                                                            >
                                                                <Select
                                                                    type="formSelect"
                                                                    allowClear
                                                                    placeholder={intl.formatMessage({ id: "option" })}
                                                                    onSelect={() => { }}
                                                                    options={[
                                                                        ...(inKindOffering || []).map((value, index) => ({
                                                                            key: index.toString(),
                                                                            label: value?.inKindOfferingDescription,
                                                                            value: value?.inKindOfferingDescription,
                                                                        })),
                                                                    ]}
                                                                />
                                                            </Form.Item>
                                                            <StyledText as="p" variant="B3" className='for'>
                                                                {intl.formatMessage({ id: "for" })}
                                                            </StyledText>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'inKindActivityDescription']}
                                                                className="formItemTextField"
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        message: intl.formatMessage({ id: "enter" }),
                                                                    },
                                                                ]}
                                                            >
                                                                <TextArea rows={1} placeholder={intl.formatMessage({ id: "enter_description" })} />
                                                            </Form.Item>
                                                            <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#04ac9c' }} className='for' />
                                                        </Space>
                                                    ))}
                                                    {inKindUseError && (
                                                        <StyledText as="p" variant="B3" className="errorText">
                                                            {inKindUseError}
                                                        </StyledText>
                                                    )}
                                                    <Form.Item>
                                                        <AntdButton htmlType='button' className='addButton' type="dashed" onClick={() => add()} block icon={<PlusOutlined fill="red" />}>
                                                            <StyledText as="p" variant="B3">
                                                                {intl.formatMessage({ id: "add" })}
                                                            </StyledText>
                                                        </AntdButton>
                                                    </Form.Item>
                                                </>
                                            )}
                                        </Form.List>
                                    </>}
                                    {checkboxGroup?.includes('volunteering') && <>
                                        <StyledText as="p" variant="B3d">{intl.formatMessage({ id: "please_update_the_volunteering_support_you_used" })}</StyledText>
                                        <Form.List name="volunteerUse">
                                            {(fields, { add, remove }) => (
                                                <>
                                                    {fields.map(({ key, name, ...restField }) => (
                                                        <Space key={key} style={{ display: 'flex', marginBottom: 0 }} className='optionsContainer'>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'volunteerUnit']}
                                                                className="formItem"
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        message: intl.formatMessage({ id: "enter" }),
                                                                    },
                                                                ]}
                                                            >
                                                                <Select
                                                                    type="formSelect"
                                                                    allowClear
                                                                    placeholder={intl.formatMessage({ id: "option" })}
                                                                    onSelect={() => { }}
                                                                    options={[
                                                                        ...(volunteeringType?.includes("General") ? [{ key: "general", label: "General", value: "General" }] : []),
                                                                        ...(volunteeringSkillList || []).map((value, index) => ({
                                                                            key: index.toString(),
                                                                            label: value,
                                                                            value: value,
                                                                        })),
                                                                    ]}
                                                                />
                                                            </Form.Item>
                                                            <StyledText as="p" variant="B3" className='for'>
                                                                {intl.formatMessage({ id: "for" })}
                                                            </StyledText>
                                                            <Form.Item
                                                                {...restField}
                                                                name={[name, 'volunteerValue']}
                                                                className="formItemTextField"
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        message: intl.formatMessage({ id: "enter" }),
                                                                    },
                                                                ]}
                                                            >
                                                                <TextArea rows={1} placeholder={intl.formatMessage({ id: "enter_description" })} />
                                                            </Form.Item>
                                                            <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#04ac9c' }} className='for' />
                                                        </Space>
                                                    ))}
                                                    {volunteeringUseError && (
                                                        <StyledText as="p" variant="B3" className="errorText">
                                                            {volunteeringUseError}
                                                        </StyledText>
                                                    )}
                                                    <Form.Item>
                                                        <AntdButton htmlType='button' className='addButton' type="dashed" onClick={() => add()} block icon={<PlusOutlined fill="red" />}>
                                                            <StyledText as="p" variant="B3">
                                                                {intl.formatMessage({ id: "add" })}
                                                            </StyledText>
                                                        </AntdButton>
                                                    </Form.Item>
                                                </>
                                            )}
                                        </Form.List>
                                    </>}
                                </>}
                                <div className='buttonContainer'>
                                    <Divider />
                                    <div className='buttonContentContainer'>
                                        <div className='nextAndPreviousContainer'>
                                            <Button
                                                variant="secondary"
                                                type="button"
                                                onClick={() => handlePrevious()}>
                                                {intl.formatMessage({ id: "previous" })}
                                            </Button>
                                            <Button
                                                variant="primary"
                                                htmlType="submit"
                                            >
                                                {intl.formatMessage({ id: "next" })}
                                            </Button>
                                        </div>
                                        <div className="saveAndExitButton">
                                            <Button
                                                variant="secondary"
                                                htmlType="button"
                                                onClick={() => handleSaveAndExit()}>
                                                {intl.formatMessage({ id: "save_n_exit" })}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="buttonContainer">
                                <Divider />
                                <div className="buttonContentContainer">
                                    <div className="nextAndPreviousContainer">
                                        <Button variant="secondary" type="button" onClick={() => handlePrevious()}>
                                            {intl.formatMessage({ id: "previous" })}
                                        </Button>
                                        <Button variant="primary" htmlType="submit">
                                            {intl.formatMessage({ id: "next" })}
                                        </Button>
                                    </div>
                                    <div className="saveAndExitButton">
                                        <Button variant="secondary" htmlType="button" onClick={() => handleSaveAndExit()}>
                                            {intl.formatMessage({ id: "save_n_exit" })}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>

                </div>
            </div>
        </div>
    )
}

const StyledCharityFinalReport = styled(CharityFinalReport)`
  &.charityFinalReport {
    background: white;
    display: flex;
    flex-direction: column;
    padding: 20px;
    justify-content: space-between;
    margin-top: 50px;
    .uploadWrapper .ant-form-item-control-input-content {
      display: flex !important;
      width: 100%;

      .ant-upload-list {
        display: flex;
        flex-direction: column !important;
      }

      .uploadInputWrapper {
        gap: 56px;
        display: flex;
        flex-direction: column;
        margin-top: 30px;
        margin-left: 30px;
        width: 100%;
      }
    }

    .mainTitle {
      margin-bottom: 20px;
    }

    .formItem .ant-form-item-explain-error {
      margin-top: -20px !important;
    }

    .contentContainer {
      display: flex;
      flex-direction: column;
      gap: 12px;
      min-height: 60vh;
      padding-bottom: 60px;

      .formContainer {
        display: flex;
        gap: 12px;
        flex-direction: column;
      }
    }

    .overlay {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1000;
      height: 100%;
      background-color: rgba(255, 255, 255, 0.45);
    }

    .spinner {
      z-index: 2000;
      margin: auto;
      left: 50%;
      right: 50%;
      position: absolute;
      bottom: 50%;
    }

    .radioGroup {
      display: flex;
      flex-direction: column;
    }

    .formItemTextField {
      .ant-form-item-control-input {
        width: 34.5vw !important;
      }
    }

    .formItemTextFieldPart {
      .ant-form-item-control-input {
        width: 16vw !important;
      }
    }

    .helperText {
      color: #5271FF !important;
      font-weight: bold !important;
      margin-bottom: 0px !important;
    }

    .ant-form-item {
      margin-bottom: 10px;
    }

    .formItemInput {
      width: 300px;
    }

    .mainTitle{
        margin-bottom: 20px;
    }
    .errorText{
        color: #ff4d4f;
    }
    .contentContainer{
        display: flex;
    }
    .addButton{
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
    }
    .formContainer{
        display: flex;
        gap: 12px;
        flex-direction: column;
    }
    .checkboxGroup{
        display: flex;
        flex-direction: column;
    }
    .optionsContainer{
        display: flex;
        align-items: center;
        gap: 30px;
        .for{
            margin-bottom: 10px;
        }
    }

    .radioGroup {
            display: flex;
            flex-direction: column;
    }

    .buttonContainer{
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        position: fixed;
        width: 100%;
        bottom: 0px;
        background: white;
        margin-left: -110px;
        .buttonContentContainer{
            display: flex;
            justify-content: center;
            width: 100%;
            position: relative;
            .saveAndExitButton{
                position: absolute;
                right: 0;
                margin-bottom: 15px;
            }
            .nextAndPreviousContainer{
                display: flex;
                gap: 20px;
                margin-bottom: 15px;
            }
        }
    }
    .formItemTextField{
        .ant-form-item-control-input{
            width: 34.5vw !important;
        }
    }
    .formItemTextFieldPart{
        .ant-form-item-control-input{
            width: 16vw !important;
        } 
    }
    .helperText {
    color: #5271FF !important;
    font-weight: bold !important;
    margin-bottom: 0px !important;
  }
  .ant-form-item{
    margin-bottom: 10px;
  }
  .tipsContainer{
    background: #D7FEFB;
    border: 1px solid #04ac9c;
    padding: 20px;
    border-radius: 10px;
    width: max-content;
    height: max-content;
    .titleContainer {
        display: flex;
        margin-bottom: 20px;
        align-items: center;
        gap: 10px;
    }
  }
  .formItemInput{
    width: 300px;
  }
  }
`;

export default StyledCharityFinalReport;
